/**
 * Phase 22 Plan 22-03 — ClientDetailPage
 *
 * Route: /clients/:id
 * Permission gate: clients.view
 *
 * 2 tabs: Profile (read-only field grid) + WOs (work orders for this client).
 */

import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useState, type JSX } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { DeactivateClientConfirmDialog } from '@/components/clients/DeactivateClientConfirmDialog';
import { ReactivateClientDialog } from '@/components/clients/ReactivateClientDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WorkOrderStatusBadge } from '@/components/work-orders/WorkOrderStatusBadge';
import { useClientQuery } from '@/hooks/useClientQueries';
import { useWorkOrdersListQuery } from '@/hooks/useWorkOrderQueries';
import { useAuth } from '@/lib/auth/AuthContext';

function formatDateTime(d: string): string {
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export function ClientDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();

  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);

  const clientQuery = useClientQuery(id);
  // WOs tab — filter by clientId using the existing WO list query
  const wosQuery = useWorkOrdersListQuery({ pageSize: 50 });

  if (!permissions.includes('clients.view')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (clientQuery.isLoading) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="client-detail-loading">
        Loading client…
      </p>
    );
  }

  if (clientQuery.isError) {
    const status = clientQuery.error.status;
    if (status === 404) {
      return (
        <div className="text-center py-16" data-testid="client-detail-not-found">
          <p className="text-lg font-medium">Client not found.</p>
          <Button asChild variant="link" className="mt-2">
            <Link to="/clients">Back to list</Link>
          </Button>
        </div>
      );
    }
    return (
      <p className="text-destructive text-sm" data-testid="client-detail-error">
        Failed to load client.
      </p>
    );
  }

  const client = clientQuery.data;
  if (client === undefined) return <></>;

  const canEdit = permissions.includes('clients.edit');
  const canDelete = permissions.includes('clients.delete');

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/clients">
            <ArrowLeftIcon size={16} /> Clients
          </Link>
        </Button>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  client.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
                data-testid="client-detail-status-badge"
              >
                {client.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-muted-foreground font-mono text-sm">{client.code}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {canEdit && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/clients/${String(id)}/edit`} data-testid="client-detail-edit-btn">
                  Edit
                </Link>
              </Button>
            )}
            {canDelete && client.isActive && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => { setDeactivateOpen(true); }}
                data-testid="client-detail-deactivate-btn"
              >
                Deactivate
              </Button>
            )}
            {canDelete && !client.isActive && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setReactivateOpen(true); }}
                data-testid="client-detail-reactivate-btn"
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="profile" data-testid="client-detail-tabs">
        <TabsList>
          <TabsTrigger value="profile" data-testid="client-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="workorders" data-testid="client-tab-workorders">Work Orders</TabsTrigger>
        </TabsList>

        {/* Profile tab */}
        <TabsContent value="profile" data-testid="client-tab-content-profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground font-medium">Name</p>
                  <p data-testid="client-detail-name">{client.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Code</p>
                  <p className="font-mono" data-testid="client-detail-code">{client.code}</p>
                </div>
                {client.contactName !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Contact Name</p>
                    <p>{client.contactName}</p>
                  </div>
                )}
                {client.contactEmail !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Contact Email</p>
                    <p>{client.contactEmail}</p>
                  </div>
                )}
                {client.contactPhone !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Contact Phone</p>
                    <p>{client.contactPhone}</p>
                  </div>
                )}
                {client.address !== null && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground font-medium">Address</p>
                    <p>{client.address}</p>
                  </div>
                )}
                {client.gstNumber !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">GST Number</p>
                    <p className="font-mono text-xs">{client.gstNumber}</p>
                  </div>
                )}
                {client.panNumber !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">PAN Number</p>
                    <p className="font-mono text-xs">{client.panNumber}</p>
                  </div>
                )}
                {client.notes !== null && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground font-medium">Notes</p>
                    <p>{client.notes}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground font-medium">Created</p>
                  <p>{formatDateTime(client.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Updated</p>
                  <p>{formatDateTime(client.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Orders tab */}
        <TabsContent value="workorders" data-testid="client-tab-content-workorders">
          <Card>
            <CardHeader>
              <CardTitle>Work Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {wosQuery.isLoading && (
                <p className="text-sm text-muted-foreground" data-testid="client-wos-loading">
                  Loading work orders…
                </p>
              )}
              {wosQuery.isError && (
                <p className="text-sm text-destructive" data-testid="client-wos-error">
                  Failed to load work orders.
                </p>
              )}
              <div className="overflow-x-auto">
                <Table data-testid="client-wos-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>WO Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(wosQuery.data?.items ?? []).length === 0 && !wosQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-6">
                          <span className="text-muted-foreground" data-testid="client-wos-empty">
                            No work orders found.
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (wosQuery.data?.items ?? []).map((wo) => (
                        <TableRow key={wo.id} data-testid={`client-wo-row-${String(wo.id)}`}>
                          <TableCell>
                            <Link
                              to={`/work-orders/${String(wo.id)}`}
                              className="text-primary hover:underline font-mono text-xs"
                            >
                              {wo.woNumber}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <WorkOrderStatusBadge status={wo.status} />
                          </TableCell>
                          <TableCell>{wo.startDate}</TableCell>
                          <TableCell>{wo.endDate}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {deactivateOpen && (
        <DeactivateClientConfirmDialog
          open={deactivateOpen}
          onOpenChange={setDeactivateOpen}
          clientId={id}
          clientName={client.name}
        />
      )}
      {reactivateOpen && (
        <ReactivateClientDialog
          open={reactivateOpen}
          onOpenChange={setReactivateOpen}
          clientId={id}
          clientName={client.name}
        />
      )}
    </div>
  );
}
